import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { ObjectData } from '../models/object-data';
import { ObjectTable } from '../models/object-table';
import { MediaContext } from '../models/media-context';

@Injectable({ providedIn: 'root' })
export class ObjectService {
  backendUrl = environment.backendUrl;
  backendUrlWithoutApi = environment.backendUrl.replace('/api', '');
  objectsUrl = this.backendUrl + '/objects';
  objectTableUrl = this.backendUrl + '/doc';
  objectUrl = this.backendUrl + '/object';
  qrCodeUrl = this.objectUrl + '/sendQrCode';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  getObjectByUuid(uuid: string): Observable<ObjectData> {
    return this.http.get<ObjectData>(`${this.objectUrl}/${uuid}`);
  }

  searchObjectByName(name: string): Observable<ObjectData[]> {
    const params = new HttpParams().set('name', name);
    return this.http.get<ObjectData[]>(this.objectsUrl, { params });
  }

  searchObjects(query: string): Observable<string[]> {
    if (!query.trim()) {
      return this.http.get<string[]>('assets/test-object-links.json');
    }
    return this.http.get<string[]>('assets/test-object-links.json').pipe(
      map(objects =>
        objects.filter(obj => obj.toLowerCase().includes(query.toLowerCase()))
      )
    );
  }

  getFirstTenObjects(): Observable<string[]> {
    return this.http.get<string[]>('assets/test-object-links.json').pipe(
      map(objects => objects.slice(0, 10))
    );
  }

  getAllObjectsData(): Observable<ObjectData[]> {
    return this.http.get<ObjectData[]>(this.objectsUrl);
  }

  getObjectContextById(
    uuid: string,
    language: string
  ): Observable<ObjectTable[]> {
    const params = new HttpParams().set('language', language);
    return this.http.get<ObjectTable[]>(`${this.objectTableUrl}/${uuid}`, {
      params
    });
  }

  getImageLinksByUuid(uuid: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.objectUrl}/${uuid}/images`);
  }

  sendQrCode(data: any): Observable<any> {
    return this.http.post(this.qrCodeUrl, {
      title: data.title,
      name: data.name,
      description: data.description,
      objectUuid: data.objectUuid,
      url: data.url
    });
  }

  loadMediaByUrl(fullUrl: string): Observable<MediaContext> {
    if (isPlatformBrowser(this.platformId)) {
      return this.http.get(fullUrl, { responseType: 'blob' }).pipe(
        map(blob => ({
          url: URL.createObjectURL(blob),
          type: blob.type,
          originUrl: fullUrl
        }))
      );
    }
    return of({
      url: fullUrl,
      type: this.detectMime(fullUrl),
      originUrl: fullUrl
    });
  }

  uploadObjectDataAndContext(
    uuid: string,
    context: ObjectTable[],
    language: string,
    object: ObjectData
  ): Observable<any> {
    const params = new HttpParams().set('language', language);
    return this.http.put<any>(
      `${this.objectUrl}/${uuid}/edit`,
      { object, context },
      { params, reportProgress: true, observe: 'events' as const }
    );
  }

  private detectMime(path: string): string {
    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'gif':
        return 'image/gif';
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      default:
        return 'application/octet-stream';
    }
  }
}